import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { processExperimentQueryParams, type ExperimentMatch } from 'experiments';

/**
 * Custom hook to check for experiment query parameters in the URL
 * and automatically set cookies when matches are found.
 * After processing, removes the experiment query parameters from the URL.
 */
export function useExperimentQueryParams(): {
  processedExperiments: ExperimentMatch[];
} {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [matches, setMatches] = useState<ExperimentMatch[]>([]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(searchParams?.toString() || '');
    const sessionKey = 'experiment-params-processed';

    // Check if there are any query params at all
    if (!urlSearchParams.toString()) {
      // Clear the flag when there are no params
      sessionStorage.removeItem(sessionKey);
      return;
    }

    const processedMatches = processExperimentQueryParams(urlSearchParams);

    if (processedMatches.length > 0) {
      // Check if we've already processed this request
      const hasProcessedAndReloaded = sessionStorage.getItem(sessionKey) === 'true';

      if (hasProcessedAndReloaded) {
        // Already processed and navigated, don't do it again to prevent infinite loop
        setMatches(processedMatches);
        // Clear the flag so future visits with params will work
        sessionStorage.removeItem(sessionKey);
        return;
      }

      setMatches(processedMatches);

      // Remove processed experiment parameters from URL
      processedMatches.forEach(match => {
        urlSearchParams.delete(match.experimentName);
      });

      // Build new URL with remaining query parameters
      const newSearch = urlSearchParams.toString();
      const newUrl = `${window.location.origin}${pathname}${newSearch ? `?${newSearch}` : ''}${
        window.location.hash
      }`;

      // Mark as processed in session storage BEFORE navigating to prevent infinite loops
      sessionStorage.setItem(sessionKey, 'true');

      // Navigate to the new URL (this will reload the page with the cleaned URL)
      window.location.href = newUrl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  return { processedExperiments: matches };
}

export default useExperimentQueryParams;
